import { energyAnalysisApiSlice as api } from '../../../apiSlice';
export const addTagTypes = ['Portfolio Analysis - Data'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      consumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGet:
        build.query<
          ConsumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGetApiResponse,
          ConsumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGetApiArg
        >({
          query: (queryArg) => ({
            url: `/consumption_data/${queryArg.analysisType}/${queryArg.accountId}/${queryArg.meterId}`,
            params: {
              start_timestamp: queryArg.startTimestamp,
              end_timestamp: queryArg.endTimestamp,
            },
          }),
          providesTags: ['Portfolio Analysis - Data'],
        }),
      getMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGet: build.query<
        GetMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGetApiResponse,
        GetMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/monthly_data/${queryArg.analysisType}/${queryArg.accountId}/${queryArg.meterId}`,
        }),
        providesTags: ['Portfolio Analysis - Data'],
      }),
      hourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGet:
        build.query<
          HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse,
          HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg
        >({
          query: (queryArg) => ({
            url: `/hour_analysis_portfolio/${queryArg.analysisType}/${queryArg.accountId}/${queryArg.meterId}`,
            params: {
              start_timestamp: queryArg.startTimestamp,
              end_timestamp: queryArg.endTimestamp,
              active_days_filter: queryArg.activeDaysFilter,
              holidays_filter: queryArg.holidaysFilter,
            },
          }),
          providesTags: ['Portfolio Analysis - Data'],
        }),
      durationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGet:
        build.query<
          DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse,
          DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg
        >({
          query: (queryArg) => ({
            url: `/duration_analysis_portfolio/${queryArg.analysisType}/${queryArg.accountId}/${queryArg.meterId}`,
            params: {
              start_timestamp: queryArg.startTimestamp,
              end_timestamp: queryArg.endTimestamp,
              active_period_filter: queryArg.activePeriodFilter,
              holidays_filter: queryArg.holidaysFilter,
            },
          }),
          providesTags: ['Portfolio Analysis - Data'],
        }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as energyAnalysisOpenApi };
export type ConsumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGetApiResponse =
  /** status 200 Successful Response */ RawDataOutput;
export type ConsumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGetApiArg =
  {
    /** The ReCalc account id to get the data for. */
    accountId: number;
    /** The metering point id to get the data for. */
    meterId: number;
    /** The type of analysis to perform. */
    analysisType: AnalysisType;
    /** Start of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    startTimestamp: string;
    /** End of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    endTimestamp: string;
  };
export type GetMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGetApiResponse =
  /** status 200 Successful Response */ MonthlyDataResponseModel;
export type GetMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGetApiArg = {
  /** The metering point id to get the data for. */
  meterId: number;
  /** The type of analysis to perform. */
  analysisType: AnalysisType;
  /** The ReCalc account id to get the data for. */
  accountId: number;
};
export type HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse =
  /** status 200 Successful Response */ HourAnalysisOutput;
export type HourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg =
  {
    /** The type of analysis to perform. */
    analysisType: AnalysisType;
    /** The ReCalc account id to get the data for. */
    accountId: number;
    /** The meter id to get the data for. */
    meterId: number;
    /** Start of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    startTimestamp: string;
    /** End of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    endTimestamp: string;
    /** Filter out data points where the timestamp is outside the active days if True. If False the data points inside the active days will be filtered out. If None no filtering will be done. */
    activeDaysFilter?: boolean | null;
    /** Filter out data points where the timestamp is not on a holiday if True. If False the data points on a holiday will be filtered out. If None no filtering will be done. */
    holidaysFilter?: boolean | null;
  };
export type DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiResponse =
  /** status 200 Successful Response */ DurationAnalysisOutput;
export type DurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetApiArg =
  {
    /** The type of analysis to perform. */
    analysisType: AnalysisType;
    /** The ReCalc account id to get the data for. */
    accountId: number;
    /** The meter id to get the data for. */
    meterId: number;
    /** Start of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    startTimestamp: string;
    /** End of the data extraction period. Format: YYYY-MM-DDTHH:MM:SS+00:00 */
    endTimestamp: string;
    /** Filter data points based on opening hours. If True, keep only data within opening hours (active period). If False, keep only data outside opening hours (inactive period). If None, no filtering is applied. */
    activePeriodFilter?: boolean | null;
    /** Filter out data points where the timestamp is not on a holiday if True. If False the data points on a holiday will be filtered out. If None no filtering will be done. */
    holidaysFilter?: boolean | null;
  };
export type TimeSeriesDataPoint = {
  /**
        The timestamp representing the value in the time series.
        Format: 2022-08-23T13:10:27+00:00
        where +00:00 is the timezone
         */
  timestamp: string;
  /**
        The corresponding value at the timestamp
         */
  value: number;
};
export type ApiWarning = {
  /** The warning message */
  message: string;
  /** The warning type */
  warning_type: string;
  /** The warning details */
  details?: object;
};
export type RawDataOutput = {
  /** The Id of the associated entity. */
  Id?: number | string | null;
  /** The name of the associated entity. */
  Name: string;
  /** The data from the meter. */
  Data: TimeSeriesDataPoint[];
  Warnings?: ApiWarning[];
};
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};
export type HttpValidationError = {
  detail?: ValidationError[];
};
export type AnalysisType = 'power' | 'heat' | 'water';
export type MonthlyDatapoint = {
  /** The year and month of the data point. */
  year_month: string;
  /** The consumption in kWh. */
  consumption: number | null;
};
export type MonthlyDataResponseModel = {
  /** The consumption data for the most recent year (0-12 months ago. */
  this_year: MonthlyDatapoint[];
  /** The consumption data for last year (12-24 months ago). */
  last_year: MonthlyDatapoint[];
  /** The consumption data for the previous year (24-36 months ago). */
  previous_year: MonthlyDatapoint[];
  /** List of warnings. */
  warnings: ApiWarning[];
};
export type TimestampsSummaryModel = {
  /** The minimum earliest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  minEarliestDataTimestamp: string | null;
  /** The maximum earliest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  maxEarliestDataTimestamp: string | null;
  /** The minimum latest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  minLatestDataTimestamp: string | null;
  /** The maximum latest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  maxLatestDataTimestamp: string | null;
};
export type HourAnalysisDataPoint = {
  /** The hour of the day. */
  HourOfDay: number;
  /** The min value of the data. */
  MinValue: number;
  /** The max value of the data. */
  MaxValue: number;
  /** The average value of the data. */
  MeanValue: number;
  /** The median of the data. */
  MedianValue: number;
  /** The first quartile of the data. */
  Q1Value: number;
  /** The third quartile of the data. */
  Q3Value: number;
};
export type HourAnalysisOutput = {
  /** The Id of the associated entity. */
  Id?: number | string | null;
  /** The name of the associated entity. */
  Name: string;
  /** The timestamp summary. */
  TimestampsSummary: TimestampsSummaryModel;
  /** The hour analysis data. */
  HourAnalysisData: HourAnalysisDataPoint[];
  Warnings?: ApiWarning[];
};
export type DurationAnalysisDataPoint = {
  /** The duration in hours. */
  Duration: number;
  /** The value of the data. */
  Value: number;
  /** The timestamp of the data point. Format: YYYY-MM-DDTHH:MM:SSZ */
  Timestamp: string;
};
export type DurationLoadProfileShare = {
  /** Share of time in hours or percentage of hours where the load profile contains energy usage belonging to active hours and active days. Null if openingHours is not configured. */
  ActivePeriod?: number | null;
  /** Share of time in hours or percentage of hours where the load profile contains energy usage belonging to outside active hours and active days. Null if openingHours is not configured. */
  InactivePeriod?: number | null;
};
export type DurationLoadProfilePoint = {
  /** The name of the load profile point. */
  Name: string;
  /** The value of the load profile point. */
  Value: number;
  /** The start duration in hours. */
  StartDuration: number;
  /** The end duration in hours. */
  EndDuration: number;
  /** The shares of the load profile in hours */
  Shares: DurationLoadProfileShare;
  /** The shares of the load profile in percentage */
  SharesPercentage: DurationLoadProfileShare;
};
export type DurationAnalysisOutput = {
  /** The Id of the associated entity. */
  Id?: number | string | null;
  /** The name of the associated entity. */
  Name: string;
  /** The timestamp summary. */
  TimestampsSummary: TimestampsSummaryModel;
  /** The duration analysis data. */
  DurationAnalysisData: DurationAnalysisDataPoint[];
  /** The load profiles for the duration analysis. */
  LoadProfiles: DurationLoadProfilePoint[];
  Warnings?: ApiWarning[];
};
export const {
  useConsumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGetQuery,
  useGetMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGetQuery,
  useHourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery,
  useDurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery,
} = injectedRtkApi;
